import { Group } from "../../../types";
import { H3 } from "../../Headers/Headings";
import { useTranslations } from "../../../contexts/Translation";
import React, { useCallback, useEffect, useId, useState } from "react";
import { HelpIcon, HelpText } from "../Input";
import styles from "./GroupSelector.module.css";

type ComponentProps = {
  groups: Group[];
  selected?: Group[];
  userId?: string;
  helpText?: React.ReactNode | string | undefined;
  onChange: (selectedGroups: Group[], added?: Group[], deleted?: Group[]) => void;
};

const GroupSelector = ({ groups, helpText, userId, selected = [], onChange }: ComponentProps) => {
  const t = useTranslations("userManagementForm");
  const [groupList, setGroupList] = useState<Group[]>(groups.filter((g) => !selected.find((s) => s.id === g.id)));
  const [initiallySelected, setInitiallySelected] = useState<Group[]>(selected);
  const [helpOpen, setHelpOpen] = useState(false);
  const helpId = useId();
  const closeHelp = useCallback(() => setHelpOpen(false), []);
  const [selectedGroups, setSelectedGroups] = useState<Group[]>(selected);

  useEffect(() => {
    setInitiallySelected(selected);
    setSelectedGroups(selected);
    setGroupList(groups.filter((g) => !selected.find((s) => s.id === g.id)));
  }, [groups, selected]);

  const addToSelected = (group: Group) => {
    const sg = [...selectedGroups, group];
    const added: Group[] = sg.filter((g) => !initiallySelected.find((s) => s.id === g.id));
    const deleted: Group[] = initiallySelected.filter((s) => !sg.find((g) => g.id === s.id));
    setSelectedGroups(sg);
    setGroupList(groupList.filter((g) => g.id !== group.id));
    onChange(sg, added, deleted);
  };

  const removeFromSelected = (group: Group) => {
    const sg = selectedGroups.filter((g) => g.id !== group.id);
    const added: Group[] = sg.filter((g) => !initiallySelected.find((s) => s.id === g.id));
    const deleted: Group[] = initiallySelected.filter((s) => !sg.find((g) => g.id === s.id));

    setGroupList([...groupList, group]);
    setSelectedGroups(sg);
    onChange(sg, added, deleted);
  };

  return (
    <div className={styles.container}>
      <H3>
        {t("groupsHeading")} <HelpIcon text={helpText} isOpen={helpOpen} setIsOpen={setHelpOpen} controls={helpId} />
      </H3>
      {helpText && <HelpText text={helpText} isOpen={helpOpen} id={helpId} onClose={closeHelp} />}
      <div className={styles.lists}>
        <div className={styles.column}>
          <H3>{t(userId ? "memberOf" : "accessFor")}</H3>
          {selectedGroups.map((g) => (
            <div className={styles.groupItem} onClick={() => removeFromSelected(g)} key={g.id}>
              {g.name}
            </div>
          ))}
        </div>
        <div className={styles.column}>
          <H3>{t("availableGroups")}</H3>
          {groupList.map((g) => (
            <div className={styles.groupItem} onClick={() => addToSelected(g)} key={g.id}>
              {g.name}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GroupSelector;
